import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import {
  getEntityData,
  saveCorrespondence,
} from "../../services/firebase/correspondence";
import { useStyles } from "../../shared/styles/useStyles";
import { IEntityData } from "../../redux/types/types";
import { IPqrsCorrespondence } from "../../interfaces/Correspondence";
import {
  allRequiredValidationSchema,
  defaultCorrespondenceType,
  defaultInternalStatus,
  defaultStatus,
  initialData,
  optionsCommunicationType,
  params,
  PqrsFormData,
  showMessage,
  someValidationSchema,
} from "./formConfiguration";
import useLocation from "./useLocation";
import { getDateToTimeZone } from "../../utils/utils";
import { CommunicationChannels, CommunicationChannelsList } from "../../enums/enums";

function usePqrs() {
  const classes = useStyles();
  const intl = useIntl();
  const {
    handleSelectCountry,
    handleSelectDepartment,
    listCountries,
    listDepartments,
    listCities,
  } = useLocation();

  const { nit } = useParams() as params;
  const [files, setFiles] = useState<File[]>([]);
  const [recaptcha, setRecaptcha] = useState<string | null>(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [entityData, setEntityData] = useState<IEntityData | null>();

  const handleRecaptcha = (token: string | null) => {
    setRecaptcha(token);
  };

  useEffect(() => {
    getEntityData(nit).then((data) => {
      if (data) {
        setEntityData(data);
      } else {
        window.location.href = "/";
      }
    });
  }, [nit]);

  const send = async (formData: PqrsFormData) => {
    if (recaptcha) {
      
      const pqrsDataForm: IPqrsCorrespondence = buildObject(formData);
      const response = await saveCorrespondence(pqrsDataForm);
      showMessage(response?.ok ?? false, intl);
    }
  };

  const buildObject = (formData: PqrsFormData) => {
    const pqrsDataForm: IPqrsCorrespondence = {
      communicationType: optionsCommunicationType.find(
        (item) => formData.communicationType === item.option
      )!,
      entityId: nit,
      description: formData.description,
      communicationChannel: CommunicationChannels.WEB_FORM,
      communicationChannelName: CommunicationChannelsList.find(
        (channel) => channel.value === CommunicationChannels.WEB_FORM
      )!.name,
      createAt: getDateToTimeZone(entityData!.country).toString(),
      files,
      isAnonymous,
      inputType: defaultCorrespondenceType,
      isParam: true,
      status: defaultStatus,
      receivingDependency: entityData?.productionOfficePQRSCiu!,
      receiveEmail: formData.receiveEmail,
      internalState: defaultInternalStatus,
      subject: intl.formatMessage({ id: formData.communicationType }),
      externalSender: !isAnonymous
        ? {
            id: formData.identificationNumber,
            idDoc: "",
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            country: formData.country,
            department: formData.department,
            city: formData.city,
          }
        : undefined,
    };

    return pqrsDataForm;
  };

  return {
    classes,
    initialData,
    someValidationSchema: someValidationSchema(intl),
    allRequiredValidationSchema: allRequiredValidationSchema(intl),
    handleSelectCountry,
    handleSelectDepartment,
    listCountries,
    listDepartments,
    listCities,
    handleRecaptcha,
    intl,
    entityData,
    isAnonymous,
    setIsAnonymous,
    optionsCommunicationType,
    recaptcha,
    setFiles,
    send,
  };
}

export default usePqrs;
